import { IconBadge, IconBadgeTone } from '../UI';

export type TabStatTone = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger';

export interface TabStat {
  key: string;
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  icon?: React.ReactNode;
  tone?: TabStatTone;
  /** Tooltip, z.B. fuer gekuerzte Werte. */
  title?: string;
  onClick?: () => void;
}

const VALUE_TONE: Record<TabStatTone, string> = {
  default: 'text-dark-50',
  primary: 'text-primary-300',
  secondary: 'text-secondary-300',
  success: 'text-emerald-300',
  warning: 'text-amber-300',
  danger: 'text-red-300'
};

/**
 * Kennzahlen-Leiste oben in den Tabs der Hundeseite (Gewicht, Zyklus, Gesundheit, DNA, Ausstellungen):
 * die wichtigsten Werte auf einen Blick, je als eigene kleine Karte. Mobil zwei Spalten, sonst so viele
 * wie nebeneinander passen. Mit onClick wird eine Kachel zum Button (z.B. Detail-Popup eines Befunds).
 */
export const TabStatStrip: React.FC<{ stats: TabStat[] }> = ({ stats }) => {
  if (stats.length === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
      {stats.map((stat) => {
        const content = (
          <>
            <span className="flex items-start justify-between gap-2">
              <span className="min-w-0 truncate text-xs font-medium text-dark-400">{stat.label}</span>
              {stat.icon && <span className="shrink-0 text-dark-400">{stat.icon}</span>}
            </span>
            <span className={`mt-1.5 block truncate text-xl font-bold tabular-nums ${VALUE_TONE[stat.tone ?? 'default']}`}>{stat.value}</span>
            {stat.hint && <span className="mt-0.5 block truncate text-xs text-dark-400">{stat.hint}</span>}
          </>
        );
        const className = 'card min-w-0 !p-4 text-left';
        return stat.onClick ? (
          <button
            key={stat.key}
            type="button"
            onClick={stat.onClick}
            title={stat.title}
            aria-haspopup="dialog"
            className={`${className} transition-colors hover:border-primary-400/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60`}
          >
            {content}
          </button>
        ) : (
          <div key={stat.key} className={className} title={stat.title}>
            {content}
          </div>
        );
      })}
    </div>
  );
};

/** Einheitlicher Kopf fuer die Karten in den Tabs: getoentes Icon, Titel mit Anzahl, Untertitel, Aktionen rechts. */
export const TabCardHeader: React.FC<{
  icon: React.ReactNode;
  tone?: IconBadgeTone;
  title: string;
  count?: number;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}> = ({ icon, tone = 'primary', title, count, subtitle, actions, className }) => (
  <div className={`mb-4 flex flex-wrap items-center justify-between gap-3 ${className ?? ''}`}>
    <div className="flex min-w-0 items-center gap-3">
      <IconBadge icon={icon} tone={tone} />
      <div className="min-w-0">
        <h2 className="flex min-w-0 items-center gap-2 text-lg font-bold text-dark-50">
          <span className="truncate">{title}</span>
          {typeof count === 'number' && count > 0 && (
            <span className="shrink-0 rounded-full bg-dark-700 px-2 py-0.5 text-xs font-semibold text-dark-300">{count}</span>
          )}
        </h2>
        {subtitle && <p className="text-xs text-dark-400">{subtitle}</p>}
      </div>
    </div>
    {/* Nicht shrink-0: passt die Zeile nicht, rutschen die Aktionen unter den Titel und brechen dort selbst um. */}
    {actions && <div className="flex max-w-full flex-wrap items-center gap-2">{actions}</div>}
  </div>
);
